import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, DollarSign, Building2, Info } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSettings() {
  const [settings, setSettings] = useState({
    payment_method: null,
    payment_iban: '',
    payment_paypal_email: '',
    payment_stripe_account_id: '',
    payment_stripe_public_key: '',
    payment_stripe_secret_key: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPaymentSettings();
  }, []);

  const fetchPaymentSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/tenants/payment-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings({
        payment_method: response.data.payment_method || null,
        payment_iban: response.data.payment_iban || '',
        payment_paypal_email: response.data.payment_paypal_email || '',
        payment_stripe_account_id: response.data.payment_stripe_account_id || '',
        payment_stripe_public_key: response.data.payment_stripe_public_key || '',
        payment_stripe_secret_key: '' // Never pre-fill for security
      });
    } catch (error) {
      toast.error('Errore nel caricamento delle impostazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/tenants/payment-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Impostazioni salvate con successo!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const paymentMethods = [
    { id: 'iban', name: 'Bonifico Bancario (IBAN)', icon: Building2 },
    { id: 'paypal', name: 'PayPal', icon: DollarSign },
    { id: 'stripe', name: 'Stripe', icon: CreditCard }
  ];

  if (loading) {
    return <div className="text-center py-8">Caricamento...</div>;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Metodo di Pagamento</CardTitle>
          <p className="text-sm text-gray-600">
            Scegli come ricevere i pagamenti dai tuoi clienti
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => setSettings({ ...settings, payment_method: method.id })}
                className={`p-4 border-2 rounded-lg transition-all hover:shadow-md ${
                  settings.payment_method === method.id
                    ? 'border-purple-600 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <method.icon className={`w-8 h-8 mb-2 ${
                  settings.payment_method === method.id ? 'text-purple-600' : 'text-gray-400'
                }`} />
                <p className="font-semibold text-sm">{method.name}</p>
              </button>
            ))}
          </div>

          {/* IBAN Section */}
          {settings.payment_method === 'iban' && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-2 text-blue-800 mb-4">
                  <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">
                    I clienti riceveranno il tuo IBAN dopo l'acquisto e dovranno effettuare il bonifico manualmente.
                    Dovrai confermare i pagamenti ricevuti.
                  </p>
                </div>

                <div>
                  <Label htmlFor="payment_iban">IBAN *</Label>
                  <Input
                    id="payment_iban"
                    value={settings.payment_iban}
                    onChange={(e) => setSettings({ ...settings, payment_iban: e.target.value })}
                    placeholder="IT60 X054 2811 1010 0000 0123 456"
                    required
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* PayPal Section */}
          {settings.payment_method === 'paypal' && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-2 text-blue-800 mb-4">
                  <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">
                    Integrare PayPal richiede un account Business PayPal. I pagamenti saranno elaborati direttamente
                    sul tuo conto PayPal.
                  </p>
                </div>

                <div>
                  <Label htmlFor="payment_paypal_email">Email PayPal Business *</Label>
                  <Input
                    id="payment_paypal_email"
                    type="email"
                    value={settings.payment_paypal_email}
                    onChange={(e) => setSettings({ ...settings, payment_paypal_email: e.target.value })}
                    placeholder="business@tuodominio.com"
                    required
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Nota:</strong> L'integrazione completa di PayPal sarà disponibile a breve.
                    Per ora puoi configurare l'email, ma i pagamenti non saranno ancora automatici.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stripe Section */}
          {settings.payment_method === 'stripe' && (
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-start gap-2 text-blue-800 mb-4">
                  <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">
                    Stripe è il metodo più professionale. I pagamenti vengono elaborati automaticamente e
                    versati sul tuo conto bancario collegato a Stripe.
                  </p>
                </div>

                <div>
                  <Label htmlFor="payment_stripe_account_id">Stripe Account ID (opzionale)</Label>
                  <Input
                    id="payment_stripe_account_id"
                    value={settings.payment_stripe_account_id}
                    onChange={(e) => setSettings({ ...settings, payment_stripe_account_id: e.target.value })}
                    placeholder="acct_xxxxxxxxxxxxx"
                  />
                  <p className="text-xs text-gray-500 mt-1">Se usi Stripe Connect</p>
                </div>

                <div>
                  <Label htmlFor="payment_stripe_public_key">Stripe Publishable Key</Label>
                  <Input
                    id="payment_stripe_public_key"
                    value={settings.payment_stripe_public_key}
                    onChange={(e) => setSettings({ ...settings, payment_stripe_public_key: e.target.value })}
                    placeholder="pk_live_xxxxxxxxxxxxx o pk_test_xxxxxxxxxxxxx"
                  />
                </div>

                <div>
                  <Label htmlFor="payment_stripe_secret_key">Stripe Secret Key</Label>
                  <Input
                    id="payment_stripe_secret_key"
                    type="password"
                    value={settings.payment_stripe_secret_key}
                    onChange={(e) => setSettings({ ...settings, payment_stripe_secret_key: e.target.value })}
                    placeholder="sk_live_xxxxxxxxxxxxx o sk_test_xxxxxxxxxxxxx"
                  />
                  <p className="text-xs text-gray-500 mt-1">Non condividere mai questa chiave</p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-yellow-800">
                    <strong>Nota:</strong> L'integrazione completa di Stripe sarà disponibile a breve.
                    Puoi già configurare le tue chiavi per essere pronto al lancio.
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex justify-end gap-4 mt-6">
            <Button
              onClick={handleSave}
              disabled={saving || !settings.payment_method}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {saving ? 'Salvataggio...' : 'Salva Impostazioni'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle>Come Funziona</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-gray-700">
          <div>
            <h4 className="font-semibold mb-2">1. Scegli il tuo metodo di pagamento</h4>
            <p>Seleziona tra IBAN, PayPal o Stripe in base alle tue preferenze.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">2. Configura i dettagli</h4>
            <p>Inserisci le informazioni necessarie (IBAN, email PayPal, chiavi Stripe).</p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">3. Ricevi i pagamenti</h4>
            <p>
              I clienti che acquistano biglietti per i tuoi eventi pagheranno direttamente sul metodo che hai scelto.
              Con Stripe i pagamenti sono automatici, con IBAN e PayPal dovrai confermare manualmente.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
